<template>
  <v-card class="mx-auto" width="450" flat>
    <div class="text-h6">
      {{
        update_alert ? `Edit ${alert.alert_name}` : device_id ? `Create Alert for ${device_label}` : `Create New Alert`
      }}
    </div>
    <v-progress-linear class="mb-4" color="primaryGrey" value="100"></v-progress-linear>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-tabs color="primary darken-2" class="text-caption" height="40" v-if="page_content">
        <v-tabs-slider color="primary darken-2"></v-tabs-slider>
        <v-tab class="text-subtitle-2">Settings</v-tab>
        <v-tab class="text-subtitle-2">Notifications</v-tab>
        <v-tab-item>
          <v-row class="mt-3 mb-0">
            <v-col cols="12">
              <v-row no-gutters>
                <v-col cols="3">
                  <v-label>Alert on</v-label>
                </v-col>
                <v-col cols="9">
                  <v-radio-group
                    class="ma-0 pa-0"
                    dense
                    mandatory
                    v-model="new_alert.alert_on"
                    row
                    @change="clearPreviousSelection($event)"
                    :disabled="update_alert"
                  >
                    <v-radio :label="`Device`" value="device"></v-radio>
                    <v-radio :label="`Tag`" value="tag"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="new_alert.alert_on == 'device'">
                <v-col cols="3">
                  <v-label>Device</v-label>
                </v-col>
                <v-col cols="9">
                  <v-text-field
                    dense
                    v-if="update_alert || device_id"
                    outlined
                    disabled
                    :value="device_label"
                  ></v-text-field>
                  <v-autocomplete
                    dense
                    v-else
                    color="primaryDark"
                    v-model="new_alert.device_id"
                    :items="devices"
                    item-text="label"
                    item-value="DeviceID"
                    @change="updateAlertID"
                    :rules="requiredRule"
                    outlined
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="new_alert.alert_on == 'tag'">
                <v-col cols="3">
                  <v-label>Tag</v-label>
                </v-col>
                <v-col cols="9">
                  <v-autocomplete
                    dense
                    v-if="device_id"
                    color="primaryDark"
                    v-model="new_alert.tag"
                    :items="device_tags"
                    item-text="label"
                    item-value="DeviceID"
                    @change="updateAlertID"
                    :rules="requiredRule"
                    outlined
                  ></v-autocomplete>
                  <v-text-field dense v-else-if="update_alert" outlined disabled :value="new_alert.tag"></v-text-field>
                  <v-autocomplete
                    dense
                    v-else
                    color="primaryDark"
                    v-model="new_alert.tag"
                    :items="tags_list"
                    item-text="label"
                    item-value="DeviceID"
                    @change="updateAlertID"
                    :rules="requiredRule"
                    outlined
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="3">
                  <v-label>Condition</v-label>
                </v-col>
                <v-col cols="9">
                  <v-autocomplete
                    class="text-caption"
                    dense
                    color="primaryDark"
                    v-model="new_alert.field"
                    :value="new_alert.field"
                    :items="comparison_fields"
                    :rules="requiredRule"
                    required
                    outlined
                    item-value="value"
                    item-text="label"
                  ></v-autocomplete>
                  <v-row no-gutters>
                    <v-col cols="6">
                      <v-select
                        class="text-caption"
                        dense
                        outlined
                        color="primaryDark"
                        v-model="new_alert.trigger_operator"
                        :items="page_content.alerts_conditions"
                        :rules="requiredRule"
                        item-value="value"
                        item-text="label"
                        required
                      ></v-select>
                    </v-col>
                    <v-spacer></v-spacer>
                    <v-col cols="5">
                      <v-text-field
                        dense
                        type="number"
                        outlined
                        v-model="new_alert.trigger_value"
                        :suffix="new_alert.field ? $config.ScvDeviceFieldsUnits[new_alert.field] : ''"
                        :rules="numberRule([-15000, 15000], true)"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-divider class="mb-4 mt-0"></v-divider>
              <v-row no-gutters>
                <v-col cols="3">
                  <v-label>Type</v-label>
                </v-col>
                <v-col cols="9">
                  <v-chip
                    v-for="type in page_content.alerts_type"
                    :key="type.value"
                    class="mb-6 mr-2"
                    label
                    color="primary"
                    :outlined="new_alert.alert_type != type.value"
                    @click="new_alert.alert_type = type.value"
                  >
                    {{ type.label }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="3">
                  <v-label>Trigger</v-label>
                </v-col>
                <v-col cols="9">
                  <div>
                    <v-chip
                      v-for="freq in page_content.alerts_trigger_frequency"
                      :key="freq.label"
                      class="mr-2"
                      color="primary"
                      label
                      :outlined="new_alert.trigger_once != freq.value"
                      @click="new_alert.trigger_once = freq.value"
                    >
                      {{ freq.label }}
                    </v-chip>
                  </div>
                  <div>
                    <v-checkbox
                      class="text-caption"
                      dense
                      v-model="show_expiration_date"
                      label="Set expiration date"
                    ></v-checkbox>
                  </div>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="show_expiration_date">
                <v-col cols="3">
                  <v-label>Expiration</v-label>
                </v-col>
                <v-col cols="9">
                  <v-menu
                    v-model="show_date_picker"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        class="text-caption"
                        v-model="new_alert.expiration_date"
                        append-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      no-title
                      v-model="new_alert.expiration_date"
                      @input="show_date_picker = false"
                      :min="min_date"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-divider class="mb-4 mt-0"></v-divider>
              <v-row no-gutters>
                <v-col cols="3">
                  <v-label>Alert Name</v-label>
                </v-col>
                <v-col cols="9">
                  <v-text-field class="text-caption" v-model="new_alert.alert_name" dense outlined></v-text-field>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="3">
                  <v-label>Message</v-label>
                </v-col>
                <v-col cols="9">
                  <v-textarea
                    class="text-caption"
                    dense
                    auto-grow
                    outlined
                    rows="3"
                    row-height="25"
                    shaped
                    v-model="new_alert.message"
                    :value="message"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item>
          <v-row class="my-2">
            <v-col cols="12">
              <v-list-item-group>
                <v-list-item dense>
                  <v-list-item-content>
                    <v-list-item-title
                      ><v-checkbox dense v-model="new_alert.push_notification" label="Notify in app"></v-checkbox
                    ></v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item dense>
                  <v-list-item-content>
                    <v-list-item-title
                      ><v-checkbox dense v-model="new_alert.send_email" label="Send email"></v-checkbox
                    ></v-list-item-title>
                    <v-list-item-subtitle v-if="new_alert.send_email"
                      ><v-text-field
                        dense
                        v-model="new_alert.user_email"
                        :value="user_email"
                        :rules="emailValidation"
                        outlined
                        placeholder="Email for notifications"
                      ></v-text-field
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item dense>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-checkbox dense disabled v-model="new_alert.webhook" label="Webhook URL"></v-checkbox
                    ></v-list-item-title>
                    <v-list-item-subtitle v-if="new_alert.webhook"
                      ><v-text-field dense v-model="new_alert.webhook_url" outlined placeholder="Webhook URL">
                      </v-text-field
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs>
    </v-form>
    <v-card-actions class="justify-end pt-0 pb-4">
      <v-btn class="mt-4" color="primary" @click="validate()" :disabled="!valid">Save</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { content } from "../../config/scavenger_alerts.js";
import { content as h2Content } from "../../config/scavenger_h2_alerts.js";
import { axios_post, axios_put } from "../services/vt_service";
import { scv } from "../services/powerdata";

export default {
  name: "ScvAlertsTemplate",
  props: {
    device_id: String,
    update_alert: Boolean,
    alert: Object,
  },
  components: {},
  watch: {
    message: function (newVal) {
      this.new_alert.message = newVal;
    },
    alert: function (newVal) {
      this.new_alert = newVal;
      if (!newVal.alert_on || newVal.alert_on == "device") {
        this.new_alert.alert_on = "device";
        this.alert_device_id = newVal.device_id;
      } else {
        this.new_alert.alert_on = "tag";
        this.alert_tag_id = newVal.tag;
      }
    },
  },
  data: function () {
    return {
      valid: true,
      alert_device_id: "",
      alert_tag_id: "",
      new_alert: {},
      show_expiration_date: false,
      show_date_picker: false,
      pattern:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      requiredRule: [(v) => !!v || this.$t("common.labels.thisFieldIsRequired")],
      emailValidation: [(v) => this.pattern.test(v) || this.$t("common.labels.emailMustBeValid")],
    };
  },
  created() {
    let device_id = this.device_id ? this.device_id : this.update_alert ? this.alert.device_id : "";
    let email = this.$auth.user ? this.$auth.user.email : "";
    let alert = this.update_alert ? this.alert : {};
    this.new_alert = {
      alert_on: alert.alert_on || "device",
      tag: alert.tag || null,
      device_id: device_id,
      alert_type: alert.alert_type || "alarm",
      trigger_once: alert.trigger_once || false,
      field: alert.field || null,
      trigger_value: alert.trigger_value || null,
      trigger_operator: alert.trigger_operator || null,
      expiration_date: alert.expiration_date || null,
      message: alert.message || "",
      alert_name: alert.alert_name || this.device_label || device_id,
      send_email: alert.send_email || false,
      push_notification: alert.push_notification || true,
      webhook: alert.webhook || false,
      webhook_url: alert.webhook_url || "",
      user_email: alert.user_email || email,
    };
    this.alert_device_id = device_id;
  },
  mounted() {},
  methods: {
    validate: function () {
      if (this.$refs.form.validate()) {
        this.saveAlert();
      }
    },
    async saveAlert() {
      let msg = null;
      let temp = { ...this.new_alert };
      temp.trigger_value = parseFloat(temp.trigger_value);
      if (this.update_alert) {
        await axios_put(scv.update_scv_alert, { id: this.alert.alert_id }, temp);
        msg = "Alert updated successfully!";
      } else {
        await axios_post(scv.add_scv_alert, temp);
        msg = "Alert added successfully!";
      }
      this.$emit("close", { msg });
    },
    clearPreviousSelection(event) {
      this.new_alert.alert_name = "";
      if (event == "tag") this.new_alert.device_id = null;
      if (event == "device") this.new_alert.tag = null;
    },
    updateAlertID() {
      if (this.new_alert.alert_on == "device") {
        this.alert_tag_id = null;
        this.alert_device_id = this.new_alert.device_id;
        this.new_alert.alert_name = this.device_label_obj[this.new_alert.device_id];
      } else {
        this.alert_device_id = null;
        this.alert_tag_id = this.new_alert.tag;
        this.new_alert.alert_name = `Devices with Tag: ${this.new_alert.tag}`;
      }
    },
    numberRule: function (digits, isRequired) {
      if (typeof digits == "number") {
        return isRequired
          ? [(v) => !!v || "This field is required", (v) => (v && v <= digits) || `Must be less than ${digits}`]
          : [(v) => !v || (v && v <= digits) || `Must be less than ${digits}`];
      } else {
        return isRequired
          ? [
              (v) => !!v || "This field is required",
              (v) =>
                (v && v <= digits[1] && v >= digits[0]) ||
                `Must be less than ${digits[1]} and greater than ${digits[0]}`,
            ]
          : [(v) => !v || (v && v <= digits[1] && v >= digits[0]) || `Must be less than ${digits}`];
      }
    },
    createAlertMessage: function (type, id, field, trigger_operator, trigger_value) {
      let trigger_message =
        type == "device" ? `Trigger alert for device: ${id}` : `Trigger alert for devices with tag: ${id}`;

      return `${trigger_message} if ${this.$config.ScvDeviceFields[field]} (${this.$config.ScvDeviceFieldsUnits[field]}) ${trigger_operator} ${trigger_value}`;
    },
    get_label: function (device) {
      return this.powerdata_type == "h2scan"
        ? `${device.AssetID ? device.AssetID : device.TransformerName ? device.TransformerName : device.DeviceID}`
        : `${device.TransformerName ? device.TransformerName : device.AssetID ? device.AssetID : device.DeviceID}`;
    },
  },
  computed: {
    powerdata_type: function () {
      return this.$store.state.tenant_config ? this.$store.state.tenant_config.powerdata_type : null;
    },
    page_content: function () {
      return this.powerdata_type == "h2scan" ? h2Content : content;
    },
    min_date: function () {
      return new Date().toISOString().split("T")[0];
    },
    devices: function () {
      return this.$store.state.devices && this.powerdata_type
        ? this.$store.state.devices
            .map((device) => {
              let label = this.get_label(device);
              return { label, ...device };
            })
            .filter((device) => device.DeviceStatus && device.DeviceStatus.toLowerCase().includes("installed"))
        : [];
    },
    device_label_obj: function () {
      return this.devices.reduce((acc, device) => {
        acc[device.DeviceID] = this.get_label(device);
        return acc;
      }, {});
    },
    device_label: function () {
      let device_id = this.device_id ? this.device_id : this.update_alert ? this.alert.device_id : null;
      return device_id ? this.device_label_obj[device_id] : "";
    },
    comparison_fields: function () {
      let fields =
        this.$store.state.tenant_config && this.$store.state.tenant_config.alert_fields
          ? this.$store.state.tenant_config.alert_fields
          : null;
      let temp = fields
        ? fields.map((elem) => {
            return {
              label: this.$config.ScvDeviceFields[elem] || elem,
              value: elem,
            };
          })
        : [];
      return temp;
    },
    user_email: function () {
      return this.$auth.user ? this.$auth.user.email : "";
    },
    message: function () {
      let trigger_operator =
        this.page_content &&
        this.page_content.alerts_conditions.filter((elem) => elem.value == this.new_alert.trigger_operator);
      let type, id;
      let message = "";
      type = this.new_alert.alert_on || "device";
      id = type == "device" ? this.device_label_obj[this.new_alert.device_id] : this.new_alert.tag;
      if (id && this.new_alert.field && this.new_alert.trigger_operator && this.new_alert.trigger_value) {
        message = this.createAlertMessage(
          type,
          id,
          this.new_alert.field,
          trigger_operator[0].label,
          this.new_alert.trigger_value
        );
      }
      return message;
    },
    devices_tags: function () {
      return this.$store.state.tags || {};
    },
    device_tags: function () {
      let device_id = this.device_id ? this.device_id : this.update_alert ? this.alert.device_id : null;
      return this.devices_tags[device_id] ? this.$store.state.tags[device_id] : [];
    },
    tags_list: function () {
      return this.$store.state.tags_list ? this.$store.state.tags_list : [];
    },
  },
};
</script>
<style scoped lang="scss"></style>
